import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/config';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  Loader2, 
  Search,
  CheckCircle2, 
  XCircle, 
  Lock,
  Unlock,
  Video,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

const FormazioneProgresso = () => {
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [videos, setVideos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [toggling, setToggling] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hw_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [videosRes, progressRes] = await Promise.all([
        axios.get(`${API}/api/training/videos`, { headers }),
        axios.get(`${API}/api/training/progress/all`, { headers })
      ]);
      
      setVideos(videosRes.data || []);
      setPartners(progressRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const toggleUnlockAll = async (managerId, currentlyUnlocked) => {
    setToggling(managerId);
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(`${API}/api/training/toggle-unlock`, {
        manager_id: managerId,
        unlock_all: !currentlyUnlocked
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(currentlyUnlocked ? 'Video bloccati (ordine sequenziale)' : 'Tutti i video sbloccati!');
      fetchData();
    } catch (error) {
      console.error('Error toggling unlock:', error);
      toast.error('Errore nel cambio stato');
    } finally {
      setToggling(null);
    }
  };

  const filtered = partners.filter(p => 
    (p.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
        <span className="ml-3 text-slate-600">Caricamento...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="formazione-progresso">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg"><Video className="h-5 w-5 text-purple-600" /></div>
            <div>
              <p className="text-2xl font-bold">{videos.length}</p>
              <p className="text-xs text-slate-500">Lezioni disponibili</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg"><Users className="h-5 w-5 text-blue-600" /></div>
            <div>
              <p className="text-2xl font-bold">{partners.length}</p>
              <p className="text-xs text-slate-500">Partner/Responsabili</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg"><CheckCircle2 className="h-5 w-5 text-green-600" /></div>
            <div>
              <p className="text-2xl font-bold">
                {partners.filter(p => p.completed_count === videos.length && videos.length > 0).length}
              </p>
              <p className="text-xs text-slate-500">Formazione completata</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Cerca partner..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
          data-testid="search-partner"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="progress-table">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-left p-3 font-medium text-slate-700 sticky left-0 bg-slate-50">Partner</th>
                  <th className="text-center p-3 font-medium text-slate-700 w-24">Progresso</th>
                  <th className="text-center p-3 font-medium text-slate-700 w-24">Punteggio</th>
                  {videos.map(v => (
                    <th key={v.id} className="text-center p-2 font-medium text-slate-700 min-w-[80px]" title={v.title}>
                      <span className="block text-xs truncate max-w-[80px]">{v.title}</span>
                    </th>
                  ))}
                  <th className="text-center p-3 font-medium text-slate-700 w-32">Sblocco</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4 + videos.length} className="text-center py-8 text-slate-500">
                      Nessun partner trovato
                    </td>
                  </tr>
                ) : (
                  filtered.map(partner => {
                    const pct = videos.length > 0 
                      ? Math.round((partner.completed_count / videos.length) * 100) 
                      : 0;
                    return (
                      <tr key={partner.id} className="border-b hover:bg-slate-50/50">
                        <td className="p-3 sticky left-0 bg-white">
                          <div>
                            <p className="font-medium text-slate-800">{partner.name}</p>
                            <p className="text-xs text-slate-400">{partner.email}</p>
                          </div>
                        </td>
                        <td className="text-center p-3">
                          <div className="flex flex-col items-center gap-1">
                            <span className="text-xs font-medium">{partner.completed_count}/{videos.length}</span>
                            <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${pct === 100 ? 'bg-green-500' : pct > 50 ? 'bg-blue-500' : pct > 0 ? 'bg-orange-500' : 'bg-slate-300'}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="text-center p-3">
                          {partner.avg_score !== null && partner.avg_score !== undefined ? (
                            <Badge className={partner.avg_score >= 80 ? 'bg-green-100 text-green-700' : partner.avg_score >= 60 ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'}>
                              {Math.round(partner.avg_score)}%
                            </Badge>
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </td>
                        {videos.map(v => {
                          const completed = (partner.completed_videos || []).includes(v.id);
                          return (
                            <td key={v.id} className="text-center p-2">
                              {completed ? (
                                <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
                              ) : (
                                <XCircle className="h-4 w-4 text-slate-300 mx-auto" />
                              )}
                            </td>
                          );
                        })}
                        <td className="text-center p-3">
                          <Button
                            size="sm"
                            variant={partner.all_unlocked ? "default" : "outline"}
                            className={partner.all_unlocked ? "bg-green-600 hover:bg-green-700 h-7 text-xs" : "h-7 text-xs"}
                            onClick={() => toggleUnlockAll(partner.id, partner.all_unlocked)}
                            disabled={toggling === partner.id}
                            data-testid={`toggle-unlock-${partner.id}`}
                          >
                            {toggling === partner.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : partner.all_unlocked ? (
                              <><Unlock className="h-3 w-3 mr-1" /> Sbloccato</>
                            ) : (
                              <><Lock className="h-3 w-3 mr-1" /> Sequenziale</>
                            )}
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FormazioneProgresso;
